import * as dotProp from 'dot-prop-immutable';
import { Action } from 'typescript-fsa';

import { createReducer } from '@topconagriculture/frontend-utils';

import { getMixersList } from '../../entities/mixers/mixers.actions';
import {
  addFeedingScheduling,
  addLoadMixer,
  deleteLoadMixer,
  getFeedingLoads,
  getLoadMixerPremixesList,
  moveFeedingScheduling,
  removeFeedingScheduling,
  resetFeedingLoads,
  resetFeedingsScheduling,
  resetLoadMixers,
  resetSelectedMixers,
  setActiveDraggingFeedingGroup,
  setActiveFeedingGroup,
  setActiveFeedingLoad,
  setActiveFeedingLoadGroup,
  setAutoExpandLoad,
  setDataTransferMethod,
  setEffectiveDate,
  setFeedingsScheduling,
  setSelectedScheduledPremix,
  updateFeedingScheduling,
} from './loadMixers.actions';
import {
  DataTransferMethods,
  DraggableFeeding,
  FeedingGroupRow,
  FeedingInScheduling,
  LoadMixer,
  LoadMixerPremix,
} from './loadMixers.types';
// tslint:disable-next-line
import { Mixer } from '../../entities/mixers/mixers.types';

export type LoadMixersState = {
  readonly feedingLoads: LoadMixer[];
  readonly selectedMixersScaleNumber: number[];
  readonly selectedScheduledPremix: string | null;
  readonly premixes: LoadMixerPremix[];
  readonly loadsForPremixCalc: LoadMixer[];
  readonly feedingsScheduling: FeedingInScheduling[];
  readonly activeFeedingLoad: LoadMixer | null;
  readonly activeFeedingLoadGroup: FeedingGroupRow | null;
  readonly activeFeedingGroup: FeedingGroupRow | null;
  readonly activeDraggingFeedingGroup: DraggableFeeding | null;
  readonly autoExpandLoad: boolean;
  readonly dataTransferMethod: DataTransferMethods | null;
  readonly effectiveDate: Date;
};

export const initialState: LoadMixersState = {
  feedingLoads: [],
  selectedMixersScaleNumber: [],
  selectedScheduledPremix: null,
  premixes: [],
  loadsForPremixCalc: [],
  feedingsScheduling: [],
  activeFeedingLoad: null,
  activeFeedingLoadGroup: null,
  activeFeedingGroup: null,
  activeDraggingFeedingGroup: null,
  autoExpandLoad: false,
  dataTransferMethod: null,
  effectiveDate: new Date(),
};

const loadMixersReducer = createReducer(initialState, {
  [getFeedingLoads.done.type]: (state: LoadMixersState, action: Action<{ result: LoadMixer[] }>) =>
    dotProp.set(state, 'feedingLoads', action.payload.result),
  [getMixersList.done.type]: (state: LoadMixersState, action: Action<{ result: Mixer[] }>) => {
    const mixers = action.payload.result.filter((mixer) => mixer.statusCode !== 4);
    return dotProp.set(
      state,
      'selectedMixersScaleNumber',
      mixers.reduce((acc: number[], curr: Mixer) => acc.concat(curr.scaleNumber), []),
    );
  },
  [addLoadMixer.type]: (state: LoadMixersState, action: Action<number>) => {
    const mixerScaleNumber = action.payload;
    return dotProp.set(state, 'selectedMixersScaleNumber', state.selectedMixersScaleNumber.concat(mixerScaleNumber));
  },
  [deleteLoadMixer.type]: (state: LoadMixersState, action: Action<number>) => {
    const mixerScaleNumber = action.payload;
    return dotProp.set(
      state,
      'selectedMixersScaleNumber',
      state.selectedMixersScaleNumber.filter((scaleNumber) => scaleNumber !== mixerScaleNumber),
    );
  },
  [resetSelectedMixers.type]: (state: LoadMixersState) => {
    if (state.selectedMixersScaleNumber.length > 1) {
      return dotProp.set(state, 'selectedMixersScaleNumber', []);
    }
    return state;
  },
  [resetFeedingLoads.type]: (state: LoadMixersState) => {
    return dotProp.set(state, 'feedingLoads', []);
  },
  [setSelectedScheduledPremix.type]: (state: LoadMixersState, action: Action<number>) => {
    return dotProp.set(state, 'selectedScheduledPremix', action.payload);
  },
  [getLoadMixerPremixesList.done.type]: (state: LoadMixersState, action: Action<{ result: LoadMixerPremix[] }>) =>
    dotProp.set(
      state,
      'premixes',
      action.payload.result.map((loadMixerPremix) => {
        return {
          ...loadMixerPremix,
          usedInFeedingPeriodIds: Array.from(new Set(loadMixerPremix.usedInFeedingPeriodIds || [])), // remove duplicates ids
        };
      }),
    ),
  [resetLoadMixers.type]: (state: LoadMixersState) => ({
    ...initialState,
  }),
  [setFeedingsScheduling.type]: (state: LoadMixersState, action: ReturnType<typeof setFeedingsScheduling>) => ({
    ...state,
    feedingsScheduling: action.payload,
  }),
  [updateFeedingScheduling.type]: (state: LoadMixersState, action: ReturnType<typeof updateFeedingScheduling>) => ({
    ...state,
    feedingsScheduling: state.feedingsScheduling.map((x) =>
      x.feedingPeriodId === action.payload.feedingPeriodId ? action.payload : x,
    ),
  }),
  [resetFeedingsScheduling.type]: (state: LoadMixersState) => ({
    ...state,
    feedingsScheduling: [],
  }),
  [moveFeedingScheduling.type]: (state: LoadMixersState, action: ReturnType<typeof moveFeedingScheduling>) => ({
    ...state,
    feedingsScheduling: state.feedingsScheduling.map((x) =>
      x.feedingPeriodId === action.payload.oldFeedingPeriodId
        ? {
            ...x,
            feedingPeriodId: action.payload.feedingPeriodId,
          }
        : x,
    ),
  }),
  [addFeedingScheduling.type]: (state: LoadMixersState, action: ReturnType<typeof addFeedingScheduling>) => ({
    ...state,
    feedingsScheduling: [...state.feedingsScheduling, action.payload],
  }),
  [removeFeedingScheduling.type]: (state: LoadMixersState, action: ReturnType<typeof removeFeedingScheduling>) => ({
    ...state,
    feedingsScheduling: state.feedingsScheduling.filter((x) => x.feedingPeriodId !== action.payload),
  }),
  [setActiveFeedingLoad.type]: (state: LoadMixersState, action: ReturnType<typeof setActiveFeedingLoad>) => ({
    ...state,
    activeFeedingLoad: action.payload,
  }),
  [setActiveFeedingLoadGroup.type]: (state: LoadMixersState, action: ReturnType<typeof setActiveFeedingLoadGroup>) => ({
    ...state,
    activeFeedingLoadGroup: action.payload,
  }),
  [setActiveFeedingGroup.type]: (state: LoadMixersState, action: ReturnType<typeof setActiveFeedingGroup>) => ({
    ...state,
    activeFeedingGroup: action.payload,
  }),
  [setActiveDraggingFeedingGroup.type]: (
    state: LoadMixersState,
    action: ReturnType<typeof setActiveDraggingFeedingGroup>,
  ) => ({
    ...state,
    activeDraggingFeedingGroup: action.payload,
  }),
  [setAutoExpandLoad.type]: (state: LoadMixersState, action: ReturnType<typeof setAutoExpandLoad>) => ({
    ...state,
    autoExpandLoad: action.payload,
  }),
  [setDataTransferMethod.type]: (state: LoadMixersState, action: ReturnType<typeof setDataTransferMethod>) => ({
    ...state,
    dataTransferMethod: action.payload,
  }),
  [setEffectiveDate.type]: (state: LoadMixersState, action: ReturnType<typeof setEffectiveDate>) => ({
    ...state,
    effectiveDate: action.payload,
  }),
});

export default loadMixersReducer;
